import React from 'react';
import { cn } from '@/utils/сn';
import { badgeSelectStyles } from './styles';
import ChevronDownIcon from '/public/icons/chevron-down.svg';

type BadgeSelectProps<Key extends string = string> = {
  label?: string;
  hint?: string;
  options: readonly { key: Key; label: string }[];
  multiple?: boolean;
  value: Key[];
  onChange: (value: Key[]) => void;
  type?: 'default' | 'secondary';
  className?: string;
  collapsible?: boolean;
  visibleCount?: number;
  showMoreButtonSize?: 'small' | 'large';
};

export function BadgeSelect<Key extends string = string>({
  label,
  hint,
  options,
  value,
  onChange,
  multiple = true,
  className,
  type = 'default',
  collapsible = false,
  visibleCount = 8,
  showMoreButtonSize = 'small',
}: BadgeSelectProps<Key>) {
  const [expanded, setExpanded] = React.useState(false);

  const toggle = (key: Key) => {
    if (multiple) {
      onChange(value.includes(key) ? value.filter((v) => v !== key) : [...value, key]);
    } else {
      onChange(value.includes(key) ? [] : [key]);
    }
  };

  const shouldCollapse = collapsible && options.length > visibleCount;
  const visibleOptions = shouldCollapse && !expanded ? options.slice(0, visibleCount) : options;

  return (
    <div className={className}>
      {label && <p className="text-body-small text-blue-900 mb-2">{label}</p>}
      {hint && <p className="text-body-small text-[#475467] mb-4">{hint}</p>}

      <div className="flex flex-wrap gap-2">
        {visibleOptions.map((option) => {
          const isActive = value.includes(option.key);

          return (
            <button
              key={option.key}
              type="button"
              onClick={() => toggle(option.key)}
              className={cn(badgeSelectStyles({ type, active: isActive }))}
            >
              {option.label}
            </button>
          );
        })}

        {shouldCollapse && (
          <button
            type="button"
            className={cn(
              'max-w-[120px] flex items-center gap-2 font-medium leading-[140%] font-inter text-blue-300 p-1 hover:bg-blue-50 cursor-pointer',
              showMoreButtonSize === 'large' ? 'text-base' : 'text-sm'
            )}
            onClick={() => setExpanded((v) => !v)}
          >
            <ChevronDownIcon className={cn('w-2.5 h-2.5 transition-transform', expanded ? 'rotate-180' : 'rotate-0')} />
            <span>{expanded ? 'Show less' : 'Show more'}</span>
          </button>
        )}
      </div>
    </div>
  );
}
